import Image from "next/image";
import { ContentfulLivePreview } from "@contentful/live-preview";
import { formatIdAttribute, getColours, richTextHelper } from "@helpers";
import type { IBannerFields } from "@types-contentful";
import React, { useState, useEffect, useRef } from "react";
import { isMobile, isTablet } from "react-device-detect";
import styled from "styled-components";
import { AppAndCTAContainer } from "./AppAndCTAContainer";
import CTAButton from "./CTAButton";
import { usePathname, useSearchParams } from "next/navigation";
import { useBrazeContentCards } from "@hooks/useBrazeContentCards";
import { useRouter } from "next/router";
import { safeJsonParse } from "@utils/safeJsonParse";

type BrazeRichTextContent = {
	content?: Array<{
		content?: Array<{
			value?: string;
		}>;
	}>;
};

const ImageWithNoCopyContainer = styled.div<{
	resetModuleMarginBottom: string;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  video {
    width: 100%;
  }
`;

const ImageWithNoCopy = styled(Image)`
  display: flex;
  flex-direction: row;
  max-height: 400px;
  margin: 0 auto;
  width: 100%;
  object-fit: cover;
  /* using max-width so that set height only applies to smaller screens */
  @media (max-width: 834px) {
    height: 264px;
  }
`;
const Container = styled.section<{
	resetModuleMarginBottom: string;
	backgroundColour: string;
	copyColourOnMobile: string;
	copyColourOnDesktop: string;
	imageAlignment: string;
	fullSizeImageMobileOrder: string;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};

  background-color: ${(props) => getColours(props.backgroundColour)};
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  grid-template-areas: ${(props) =>
		props.fullSizeImageMobileOrder === "Content First"
			? `'content'
     'image'`
			: `'image'
     'content'`};

  h1,
  p {
    color: ${(props) => getColours(props.copyColourOnMobile)};
    @media (min-width: 834px) {
      color: ${(props) => getColours(props.copyColourOnDesktop)};
    }
  }

  @media (min-width: 834px) {
    grid-template-areas: ${(props) =>
			props.imageAlignment === "Left" ? `"image content"` : `"content image"`};
  }

  h1 {
    margin-bottom: 24px;
  }

  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`;

const ImageSection = styled.section`
  grid-area: image;

  @media (max-width: 833px) {
    display: none;
  }
`;
const MobileImageSection = styled.section<{ imageOnMobile: boolean }>`
  grid-area: image;

  @media (max-width: 833px) {
    display: ${(props) => props.imageOnMobile === false && "none"};
  }
  @media (min-width: 834px) {
    display: none;
  }
`;
const ImageContainer = styled.div`

	position: relative;
	width: 100%;
	height: 100%;
	object-fit: cover;

`;

const ContentSection = styled.section<{
	imageAlignment: string;
	noTopMargin: any;
}>`
  align-self: center;
  grid-area: content;
  padding: 40px 35px;
  .placeholder {
	display: none;
  }
  @media (min-width: 834px) {
    padding: ${(props) =>
			props.imageAlignment === "Left" ? "8% 20% 8% 7%" : "8% 7% 8% 20%"};
	.placeholder {
		display: block;
  	}
  }
  a {
    margin-top: ${(props) => (props.noTopMargin ? "0" : "32px")};
  }
`;

const FullSizeImageContainer = styled.div<{
	resetModuleMarginBottom: string;
	backgroundColour: string;
	copyColourOnMobile: string;
	copyColourOnDesktop: string;
	fullSizeImageMobileOrder: string;
	ImageOpacity: any;
}>`
  ${({ resetModuleMarginBottom }) =>
		resetModuleMarginBottom === "Yes" && "margin-bottom: 0px !important"};
  background-color: ${(props) => getColours(props.backgroundColour)};
  display: flex;
  flex-direction: column;
  flex-direction: ${(props) =>
		props.fullSizeImageMobileOrder === "Image First" && "column-reverse"};
  max-height: 100vh;

  h1,
  p {
    color: ${(props) => getColours(props.copyColourOnMobile)};
    @media (min-width: 834px) {
      color: ${(props) => getColours(props.copyColourOnDesktop)};
    }
  }

  @media (min-width: 834px) {
    /* the 4d at the end of #000000 adds an opacity of 30%*/
    background-color: ${(props) =>
			props.ImageOpacity ? "#0000004d" : "#00000000"};
    display: grid;
    place-items: center;

    > * {
      grid-column: 1 / -1;
      grid-row: 1 / -1;
    }
    img,
    video {
      max-height: 100vh;
      object-fit: cover;
      width: 100%;
      z-index: -1;
    }
  }
`;

const Span = styled.span<{ textAlignment: string }>`
  padding: 8% 35px;
  text-align: ${(props) => props.textAlignment === "center" && "center"} !important;
  h1 {
    margin-bottom: 24px;
  }
  a {
    margin: ${(props) =>
			props.textAlignment === "center" ? "32px auto" : "32px 0 0 0"} !important;
  }
  @media (min-width: 834px) {
    align-self: center;
    justify-self: ${(props) => props.textAlignment} !important;
    justify-self: flex-start;
    margin-left: 100px;
    margin-right: 100px;
    padding: 0;
    width: 40%;
  }
`;

const QRCodeImage = styled.div`
  width: 150px;
  height: 150px;
  margin-top: 20px;
  img {
    margin-top: 30px;
  }
`;

const BannerContent = styled.div`
	strong {
		font-weight: bold;
	}
`;

const SubCopy = styled.div`
  margin-top: 32px;

  p,
  span {
    font-size: 14px;
  }
`;

const loaderProp = ({ src }) => {
	return src;
};

const LazyLoadedMedia = ({ src, alt, type, width, height }) => {
	const [isIntersecting, setIsIntersecting] = useState(false);

	const mediaRef = useRef(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsIntersecting(true);
					if (mediaRef.current) {
						observer.unobserve(mediaRef.current);
					}
				}
			},
			{ threshold: 0.1 },
		);

		if (mediaRef.current) {
			observer.observe(mediaRef.current);
		}

		return () => {
			if (mediaRef.current) {
				observer.unobserve(mediaRef.current);
			}
		};
	}, []);

	if (type === "video") {
		return (
			<video
				ref={mediaRef}
				muted
				loop
				autoPlay={isIntersecting}
				playsInline
				preload="none"
			>
				{isIntersecting && <source src={src} type="video/mp4" />}
			</video>
		);
	}
	return (
		<div ref={mediaRef}>
			{isIntersecting && (
				<img src={src} alt={alt} width={width} height={height} />
			)}
		</div>
	);
};

// Helper function to parse Braze content which might be HTML or JSON
const parseBrazeContent = (content: string | undefined): string => {
	if (!content) return "";

	const parsed = safeJsonParse<BrazeRichTextContent>(
		content,
		{},
		{ component: "Banner", action: "parse_braze_content" },
	);

	if (!parsed.success) {
		return content;
	}

	if (parsed.value.content?.[0]?.content) {
		return parsed.value.content[0].content
			.map((item) => item.value || "")
			.join("");
	}

	return content;
};

// Helper to normalize banner canvas key (similar to ContentCardModule)
const normalizeBannerKey = (raw: string): string => {
	const k = (raw || "")
		.toString()
		.trim()
		.toLowerCase()
		.replace(/bannerr/g, "banner"); // fix common typo
	if (!k || k === "true") return "banner_default";
	return k;
};

const Banner = ({
	name,
	removeNegativeSpacingBelowModule,
	fullSizeImage,
	fullSizeImageWithoutCopy,
	fullSizeImageCopyAlignment,
	fullSizeImageMobileOrder,
	fullSizeImageOpacity,
	image,
	imageAlignment,
	backgroundColour,
	headline,
	mainText,
	copyColourOnDesktop,
	copyColourOnMobile,
	ctaButtonColour,
	ctaButtonLabel,
	ctaButtonUrl,
	switchAppStoreCtaOn,
	appStoreUrl,
	googlePlayUrl,
	qrCodeImage,
	removeImageFromMobile,
	subCopy,

	//@ts-ignore
	id,
	//@ts-ignore
	carouselIndex,
}: IBannerFields & { carouselIndex?: number }) => {
	const [deviceType, setDeviceType] = useState("mobile");

	const pathname = usePathname();
	const router = useRouter();
	const currentPath = router?.asPath || pathname || "";

	const searchParams = useSearchParams();

	// Braze integration
	const { loading, getCardsByType, logCardClick, logCardImpression } =
		useBrazeContentCards();

	// Stable id for this module instance - use Contentful id if available, otherwise generate
	const instanceIdRef = useRef<string>("");
	if (!instanceIdRef.current) {
		instanceIdRef.current =
			id || `banner-${Math.random().toString(36).slice(2)}`;
	}

	// Track which cards have logged impressions
	const loggedImpressionCardIds = useRef<Set<string>>(new Set());

	// Fetch home_main_banner cards from Braze
	const brazeBannerCards = getCardsByType("home_main_banner");

	// Filter by page_names and get unique banners by position
	const uniqueBanners = React.useMemo(() => {
		// Filter by page_names
		const matchingCards = brazeBannerCards.filter((card) => {
			const pageNames = card.extras?.page_names;
			if (!pageNames) return false;

			const pages = pageNames.split(",").map((p) => p.trim());

			return pages.some((page) => {
				if (page === currentPath) return true;
				if (page === "/*") return true;
				return false;
			});
		});

		// Remove duplicates by combination of braze_banner and position
		// This allows multiple variants per banner group (e.g., banner_1 with position banner_1 and banner_2)
		const uniqueMap = new Map();
		for (const card of matchingCards) {
			const brazeBanner = (card.extras?.braze_banner || "").toString().trim();
			const position = (card.extras?.position || "unknown").toString().trim();
			// Create unique key from both braze_banner and position
			const uniqueKey = `${brazeBanner}:${position}`;
			if (!uniqueMap.has(uniqueKey)) {
				uniqueMap.set(uniqueKey, card);
			}
		}

		// Sort by braze_banner first, then by position
		const sorted = Array.from(uniqueMap.values()).sort((a, b) => {
			const brazeA = (a.extras?.braze_banner || "").toString().trim();
			const brazeB = (b.extras?.braze_banner || "").toString().trim();
			const posA = (a.extras?.position || "").toString().trim();
			const posB = (b.extras?.position || "").toString().trim();

			// First sort by braze_banner
			const brazeCompare = brazeA.localeCompare(brazeB);
			if (brazeCompare !== 0) return brazeCompare;

			// Then sort by position within the same braze_banner group
			return posA.localeCompare(posB);
		});

		return sorted;
	}, [brazeBannerCards, currentPath]);

	// Distribute Braze banners across multiple Banner components
	// Each Banner component matches banners by their position value (banner_1, banner_2, etc.)
	let allowBrazeForThisModule = false;
	let brazeBanner: (typeof brazeBannerCards)[0] | null = null;

	if (!loading && uniqueBanners.length > 0 && currentPath) {
		try {
			const w: any = window;
			// Registry to track which banner position each module has claimed
			w.__brazeBannerPositionMap = w.__brazeBannerPositionMap || new Map();
			w.__brazeBannerOwnersMap = w.__brazeBannerOwnersMap || new Map();

			const positionMap: Map<string, number> = w.__brazeBannerPositionMap;
			const ownersMap: Map<
				string,
				Map<string, string>
			> = w.__brazeBannerOwnersMap;

			const claimedPositions = positionMap.get(currentPath) || 0;
			const ownersForPage =
				ownersMap.get(currentPath) || new Map<string, string>();

			// Determine this module's position (banner_1, banner_2, etc.)
			// based on sequential order of Banner components on the page
			let modulePosition: string | null = null;
			let alreadyClaimedPosition: string | null = null;

			// Check if this module already claimed a position
			for (const [position, ownerId] of Array.from(ownersForPage.entries())) {
				if (ownerId === instanceIdRef.current) {
					alreadyClaimedPosition = position;
					break;
				}
			}

			if (alreadyClaimedPosition) {
				// This module already claimed a position, use it
				modulePosition = alreadyClaimedPosition;
			} else if (carouselIndex !== undefined) {
				// If this Banner is in a carousel, use the carousel index to determine position
				// carouselIndex 0 = banner_1, carouselIndex 1 = banner_2, etc.
				modulePosition = `banner_${carouselIndex + 1}`;
				positionMap.set(
					currentPath,
					Math.max(claimedPositions, carouselIndex + 1),
				);
				ownersForPage.set(modulePosition, instanceIdRef.current);
				ownersMap.set(currentPath, ownersForPage);
			} else {
				// Assign the next sequential position (banner_1, banner_2, etc.)
				modulePosition = `banner_${claimedPositions + 1}`;
				positionMap.set(currentPath, claimedPositions + 1);
				ownersForPage.set(modulePosition, instanceIdRef.current);
				ownersMap.set(currentPath, ownersForPage);
			}

			// Find a banner that matches this module's position
			// Try to find exact match first, then fall back to any available banner
			if (modulePosition) {
				// First, try to find a banner with matching position
				const matchingBanner = uniqueBanners.find(
					(banner) =>
						(banner.extras?.position || "").toString().trim() ===
						modulePosition,
				);

				if (matchingBanner) {
					brazeBanner = matchingBanner;
					allowBrazeForThisModule = true;
				} else {
					// If no exact match, check if there are any unclaimed banners
					// by looking for banners that haven't been claimed by other modules
					const claimedBannerIds = new Set<string>();
					for (const [pos, ownerId] of Array.from(ownersForPage.entries())) {
						if (ownerId !== instanceIdRef.current) {
							// Find which banner was claimed by this other owner
							const claimedBanner = uniqueBanners.find(
								(b) => (b.extras?.position || "").toString().trim() === pos,
							);
							if (claimedBanner) {
								claimedBannerIds.add(claimedBanner.id);
							}
						}
					}

					// Find first unclaimed banner
					const unclaimedBanner = uniqueBanners.find(
						(b) => !claimedBannerIds.has(b.id),
					);

					if (unclaimedBanner) {
						brazeBanner = unclaimedBanner;
						allowBrazeForThisModule = true;
					}
				}
			}
		} catch {
			// fallback to not allowing if something unexpected happens
			allowBrazeForThisModule = false;
		}
	}

	// Force re-render when Braze data becomes available to ensure client-side updates
	const [brazeDataReady, setBrazeDataReady] = useState(false);
	useEffect(() => {
		if (!loading && uniqueBanners.length > 0) {
			setBrazeDataReady(true);
		}
	}, [loading, uniqueBanners.length]);
	// Log impressions when Braze banners are rendered (once per card)
	useEffect(() => {
		if (
			allowBrazeForThisModule &&
			brazeBanner &&
			brazeBanner.id &&
			!loggedImpressionCardIds.current.has(brazeBanner.id)
		) {
			logCardImpression(brazeBanner.id);
			loggedImpressionCardIds.current.add(brazeBanner.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allowBrazeForThisModule, brazeBanner]);

	const [referrerName, setReferrerName] = useState<string | null>(null);
	const [promoCode, setPromoCode] = useState<string | null>(null);
	const [finalHeadline, setFinalHeadline] = useState<string | null>(null);
	const [isMobileViewport, setIsMobileViewport] = useState(false);

	// Determine content source: Braze if available, otherwise Contentful
	const bannerHeadline = brazeBanner
		? parseBrazeContent(brazeBanner.title)
		: headline;
	const bannerMainText = brazeBanner
		? brazeBanner.description
		: typeof mainText === "string"
			? mainText
			: undefined;
	const bannerImageUrl = brazeBanner?.image;
	// Braze Action: "Link Text" is in brazeBanner.linkText, "Link" is in brazeBanner.url
	const bannerCtaUrl = brazeBanner
		? brazeBanner.url || ctaButtonUrl
		: ctaButtonUrl;
	const bannerCtaLabel = brazeBanner
		? brazeBanner.linkText || ctaButtonLabel
		: ctaButtonLabel;

	// Extract parameters on component mount
	useEffect(() => {
		const referrer = searchParams ? searchParams.get("referrer") : null;
		const code = searchParams ? searchParams.get("code") : null;

		if (referrer) {
			setReferrerName(referrer);
			setFinalHeadline(`${referrer} has referred you to felix`);
		} else {
			setFinalHeadline(bannerHeadline || headline);
		}

		if (code) {
			setPromoCode(code);
		}
	}, [searchParams, bannerHeadline, headline]);

	// Modify CTA URL to include parameters if they exist
	const getModifiedCtaUrl = () => {
		const urlToUse = bannerCtaUrl || ctaButtonUrl;
		if (!urlToUse) return null;

		try {
			// Check if we have parameters to add
			if (!promoCode && !referrerName) return urlToUse;

			const isAbsoluteUrl = urlToUse.startsWith("http");
			const url = new URL(
				isAbsoluteUrl ? urlToUse : `https://example.com${urlToUse}`,
			);

			// Build the URL manually to prevent encoding of the equals sign
			let finalUrl = url.pathname;
			const params: string[] = [];

			// Add promo code if it exists (using direct string concatenation to avoid encoding)
			if (promoCode) {
				params.push(`rc=${promoCode}endrc`);
			}

			// Add parameters to URL
			if (params.length > 0) {
				finalUrl += `?${params.join("&")}`;
			}

			// Add any original search params except ones we're explicitly setting
			const originalSearchParams = new URLSearchParams(url.search);
			originalSearchParams.delete("promo");
			originalSearchParams.delete("ref");

			const remainingParams = originalSearchParams.toString();
			if (remainingParams && params.length > 0) {
				finalUrl += `&${remainingParams}`;
			} else if (remainingParams) {
				finalUrl += `?${remainingParams}`;
			}

			// Return full URL if it was absolute, otherwise just the path
			if (isAbsoluteUrl) {
				return `${url.protocol}//${url.host}${finalUrl}`;
			}
			return finalUrl;
		} catch (error) {
			console.error("Error modifying CTA URL:", error);
			return urlToUse; // Return original URL if there's an error
		}
	};

	const renderHeadline = () => {
		// Use Braze headline if available, otherwise use finalHeadline (which handles referrer cases)
		const headlineToDisplay = brazeBanner
			? parseBrazeContent(brazeBanner.title)
			: finalHeadline || headline;

		if (!headlineToDisplay || headlineToDisplay === null) {
			return (
				<h1
					style={{
						visibility: "hidden",
						marginBottom: "24px",
					}}
					data-testid={`${testId}-header`}
					aria-label={
						headline /* Provide accessible content for screen readers */
					}
				>
					{headline /* Use the default headline as placeholder */}
				</h1>
			);
		}

		return (
			<h1
				id={formatIdAttribute(headlineToDisplay)}
				{...ContentfulLivePreview.getProps({
					entryId: id,
					fieldId: "title",
				})}
			>
				{headlineToDisplay}
			</h1>
		);
	};

	// Create a unique data-testid based on the button text and position
	const testId = `cta-banner-${name || ""}-${id || ""}-${pathname || ""}`
		.toString()
		.toLowerCase()
		.replace(/\s+/g, "-");

	useEffect(() => {
		if (isMobile || isTablet) {
			setDeviceType("mobile");
		} else {
			setDeviceType("desktop");
		}
	}, []);

	useEffect(() => {
		const mediaQuery = window.matchMedia("(max-width: 834px)");

		const updateViewport = (event?: MediaQueryListEvent) => {
			setIsMobileViewport(event ? event.matches : mediaQuery.matches);
		};

		updateViewport();
		mediaQuery.addEventListener("change", updateViewport);

		return () => {
			mediaQuery.removeEventListener("change", updateViewport);
		};
	}, []);

	// loading resolves before the first paint (via useLayoutEffect in the hook),
	// so this effectively decides Braze vs Contentful without any visible blur or flash.
	const showBlurredPlaceholder = loading;

	//For when full size image is chosen in contentful:
	const isFullSizeImage = fullSizeImage === "Yes";

	// Determine image source: Braze if available, otherwise Contentful
	const imageSource =
		brazeBanner && bannerImageUrl ? bannerImageUrl : image?.fields?.file?.url;

	const mediaType = image?.fields?.file?.contentType;
	const videoMedia = mediaType === "video/mp4";
	// Note: Braze images are assumed to be images, not videos

	const opacityForFullSizeImage = fullSizeImageOpacity === "Yes";

	const altAttribute =
		image?.fields?.description !== ""
			? image?.fields?.description
			: image?.fields?.title;

	const resetModuleMarginBottom = removeNegativeSpacingBelowModule;

	const imageOnMobile =
		removeImageFromMobile === "No" ||
		removeImageFromMobile === null ||
		removeImageFromMobile === undefined;

	const AppStoreCtaOn = switchAppStoreCtaOn;

	let textAlignment;
	switch (fullSizeImageCopyAlignment) {
		case "Left":
			textAlignment = "flex-start";
			break;
		case "Right":
			textAlignment = "flex-end";
			break;
		default:
			textAlignment = "center";
	}

	const showQRCode = deviceType === "desktop" && qrCodeImage;

	// Get the modified CTA URL
	const modifiedCtaUrl = getModifiedCtaUrl();

	const renderContent = () => {
		// Return the content section with modified headline and CTA URL
		return (
			<ContentSection
				imageAlignment={imageAlignment || "Left"}
				noTopMargin={AppStoreCtaOn}
			>
				{renderHeadline()}

				{brazeBanner && bannerMainText ? (
					<BannerContent
						dangerouslySetInnerHTML={{
							__html: parseBrazeContent(bannerMainText),
						}}
					/>
				) : (
					<>
					<span data-testid={`${testId}-copy`}>{richTextHelper(mainText)}</span>
					
					</>
				)}
				{modifiedCtaUrl && bannerCtaLabel && !AppStoreCtaOn && (
					<>
					<CTAButton
						url={modifiedCtaUrl}
						ctaButtonColour={ctaButtonColour || "Watermelon"}
						gtmCtaPosition={name || "no position provided in contentful"}
						gtmCtaText={bannerCtaLabel}
						preserveAbsoluteUrl={!!brazeBanner}
						onClick={() => {
							if (brazeBanner?.id) {
								logCardClick(brazeBanner.id);
							}
						}}
					>
						{bannerCtaLabel}
					</CTAButton>
					<SubCopy>{subCopy && richTextHelper(subCopy)}</SubCopy>
					</> 
				)}
				{!showQRCode && AppStoreCtaOn ? (
					<AppAndCTAContainer
						//@ts-ignore
						style={{ marginTop: "0 !important" }}
						name={name}
						googlePlayUrl={googlePlayUrl}
						appStoreUrl={appStoreUrl}
						position="relative"
						bgColor={backgroundColour}
						justifyContent="flex-start"
						downloadTextDesktop="download our app"
						downloadText="download our app"
						showWebSignupLink={false}
					/>
				) : (
					AppStoreCtaOn && (
						<QRCodeImage>
							<Image
								src={
									qrCodeImage?.fields?.file?.url.startsWith("http")
										? qrCodeImage?.fields?.file?.url
										: `https:${qrCodeImage?.fields?.file?.url}`
								}
								width={150}
								height={150}
								alt={altAttribute}
							/>
						</QRCodeImage>
					)
				)}
			</ContentSection>
		);
	};

	// Render full page image without copy
	if (fullSizeImageWithoutCopy === "Yes") {
		const imageSrc = imageSource?.startsWith("http")
			? imageSource
			: imageSource
				? `https:${imageSource}`
				: "";

		// Use Contentful image while loading (with blur), then show final content
		const displayImageSrc = showBlurredPlaceholder
			? image?.fields?.file?.url?.startsWith("http")
				? image?.fields?.file?.url
				: image?.fields?.file?.url
					? `https:${image?.fields?.file?.url}`
					: ""
			: imageSrc;

		return (
			<ImageWithNoCopyContainer
				data-testid={testId}
				resetModuleMarginBottom={resetModuleMarginBottom}
				style={{
					// Reserve space while loading to prevent layout shift
					minHeight: isMobileViewport ? undefined : "416px",
					filter: showBlurredPlaceholder ? "blur(20px)" : "none",
				}}
			>
				{displayImageSrc &&
					(!brazeBanner && videoMedia && !showBlurredPlaceholder ? (
						<LazyLoadedMedia
							src={displayImageSrc}
							alt={altAttribute}
							type={videoMedia ? "video" : "image"}
							width={833}
							height={416}
						/>
					) : (
						<ImageWithNoCopy
							src={displayImageSrc}
							alt=""
							loader={loaderProp}
							width={833}
							height={416}
							data-testid={`${testId}-image`}
						/>
					))}
			</ImageWithNoCopyContainer>
		);
	}

	// Render full size image with copy
	if (isFullSizeImage) {
		// Use Contentful content while loading (with blur), then show final content
		const displayImageSrc = showBlurredPlaceholder
			? image?.fields?.file?.url?.startsWith("http")
				? image?.fields?.file?.url
				: image?.fields?.file?.url
					? `https:${image?.fields?.file?.url}`
					: ""
			: imageSource?.startsWith("http")
				? imageSource
				: imageSource
					? `https:${imageSource}`
					: "";

		return (
			<FullSizeImageContainer
				resetModuleMarginBottom={resetModuleMarginBottom || "No"}
				fullSizeImageMobileOrder={fullSizeImageMobileOrder}
				backgroundColour={backgroundColour || "White"}
				copyColourOnDesktop={copyColourOnDesktop || "Midnight Blue"}
				copyColourOnMobile={copyColourOnMobile || "Midnight Blue"}
				ImageOpacity={opacityForFullSizeImage}
				data-testid={testId}
				style={{
					// Reserve space while loading to prevent layout shift
					minHeight: "500px",
					filter: showBlurredPlaceholder ? "blur(20px)" : "none",
				}}
			>
				{displayImageSrc && (
					<>
						{!brazeBanner && videoMedia && !showBlurredPlaceholder ? (
							<video muted loop autoPlay={true} preload="true" playsInline>
								<source src={displayImageSrc} type="video/mp4" />
							</video>
						) : (
							<img
								src={displayImageSrc}
								alt=""
								style={{ height: "100%", width: "100%" }}
							/>
						)}
						<Span textAlignment={textAlignment}>
							{showBlurredPlaceholder ? (
								<>
									{headline && <h1>{headline}</h1>}
									{mainText && richTextHelper(mainText)}
									{subCopy && richTextHelper(subCopy)}
								</>
							) : (
								<>
									{renderHeadline()}

									{brazeBanner && bannerMainText ? (
										<BannerContent
											dangerouslySetInnerHTML={{
												__html: parseBrazeContent(bannerMainText),
											}}
										/>
									) : (
										mainText && richTextHelper(mainText)
									)}
									{modifiedCtaUrl && bannerCtaLabel && (
										<CTAButton
											url={modifiedCtaUrl}
											ctaButtonColour={ctaButtonColour || "Watermelon"}
											gtmCtaPosition={
												name || "no position provided in contentful"
											}
											gtmCtaText={bannerCtaLabel}
											preserveAbsoluteUrl={!!brazeBanner}
											onClick={() => {
												if (brazeBanner?.id) {
													logCardClick(brazeBanner.id);
												}
											}}
										>
											{bannerCtaLabel}
										</CTAButton>
									)}
								</>
							)}
						</Span>
					</>
				)}
			</FullSizeImageContainer>
		);
	}

	// Render standard banner
	const imageSrc = imageSource?.startsWith("http")
		? imageSource
		: imageSource
			? `https:${imageSource}`
			: "";

	// Use Contentful content while loading (with blur), then show final content
	const displayImageSrc = showBlurredPlaceholder
		? image?.fields?.file?.url?.startsWith("http")
			? image?.fields?.file?.url
			: image?.fields?.file?.url
				? `https:${image?.fields?.file?.url}`
				: ""
		: imageSrc;

	return (
		<Container
			resetModuleMarginBottom={resetModuleMarginBottom || "No"}
			backgroundColour={backgroundColour || "White"}
			imageAlignment={imageAlignment || "Left"}
			copyColourOnDesktop={copyColourOnDesktop || "Midnight Blue"}
			copyColourOnMobile={copyColourOnMobile || "Midnight Blue"}
			fullSizeImageMobileOrder={fullSizeImageMobileOrder}
			data-testid={testId}
			style={{
				// Reserve space while loading to prevent layout shift
				minHeight: "500px",
				filter: showBlurredPlaceholder ? "blur(20px)" : "none",
			}}
		>
			{displayImageSrc && (
				<>
					<ImageSection>
						<ImageContainer>
							<img
								src={displayImageSrc}
								alt=""
								width={833}
								height={416}
								data-testid={`${testId}-image`}
							/>
						</ImageContainer>
					</ImageSection>
					<MobileImageSection imageOnMobile={imageOnMobile}>
						<img
							src={displayImageSrc}
							alt=""
							width={833}
							height={416}
							data-testid={`${testId}-image-mobile`}
						/>
					</MobileImageSection>
				</>
			)}
			{showBlurredPlaceholder ? (
				<ContentSection
					imageAlignment={imageAlignment || "Left"}
					noTopMargin={AppStoreCtaOn}
				>
					{headline && (
						<h1
							id={formatIdAttribute(headline)}
							{...ContentfulLivePreview.getProps({
								entryId: id,
								fieldId: "title",
							})}
						>
							{headline}
						</h1>
					)}
					{mainText && richTextHelper(mainText)}
					{subCopy && richTextHelper(subCopy)}
				</ContentSection>
			) : (
				renderContent()
			)}
		</Container>
	);
};

export default Banner;
